import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Volume2, VolumeX, X, Sparkles, HelpCircle, Send, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { GUIDES, DEFAULT_GUIDE } from "@/config/guides";
import { Modal, GoldButton } from "@/components/ui-kit";
import api from "@/lib/api";
import { toast } from "sonner";

export function PageGuide() {
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const utterRef = useRef(null);
  const audioRef = useRef(null);

  const submitReport = async () => {
    if (!reportText.trim()) return;
    try { await api.post("/support/report-issue", { message: reportText, path: pathname }); toast.success(t("Signalement envoyé")); }
    catch { toast.error(t("Erreur")); }
    setReportOpen(false); setReportText("");
  };

  const key = pathname.startsWith("/client/") ? "/clients" : pathname;
  const g = (GUIDES[key] || DEFAULT_GUIDE)[lang] || (GUIDES[key] || DEFAULT_GUIDE).fr;

  const stop = () => {
    try { window.speechSynthesis.cancel(); } catch {}
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    setSpeaking(false);
  };
  useEffect(() => () => stop(), []);
  useEffect(() => { stop(); }, [pathname]);

  const fallbackSpeak = (text) => {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "en" ? "en-US" : "fr-CA";
    u.rate = 1;
    u.onend = () => setSpeaking(false);
    utterRef.current = u;
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const speak = async () => {
    stop();
    const text = `${g.title}. ${g.intro} ${g.steps.join(". ")}`;
    setLoadingVoice(true);
    try {
      const { data } = await api.post("/tts", { text, voice: lang === "en" ? "nova" : "sage" });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      setSpeaking(true);
      await audio.play();
    } catch {
      fallbackSpeak(text);
    } finally { setLoadingVoice(false); }
  };

  return (
    <div className="relative">
      <button data-testid="page-guide-btn" onClick={() => setOpen(!open)} title={t("Guide de la page")}
        className={`relative transition-colors ${open ? "text-[#F2BC2E]" : "text-gray-400 hover:text-[#F2BC2E]"}`}>
        <Volume2 size={18} />
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#F2BC2E] animate-pulse" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => { setOpen(false); stop(); }} />
          <div className="fixed left-2 right-2 top-[62px] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 w-auto sm:w-[340px] bg-[#141414] border border-white/10 rounded-2xl overflow-hidden z-[60] shadow-2xl pg-fade-up" data-testid="page-guide-panel">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-[#F2BC2E]/10 to-transparent">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F2BC2E] to-[#F10D19] flex items-center justify-center"><Sparkles size={14} className="text-black" /></div>
                <span className="font-display font-bold text-sm text-white">{t("Guide IA")} · {g.title}</span>
              </div>
              <button onClick={() => { setOpen(false); stop(); }} className="text-gray-500 hover:text-white" data-testid="guide-close"><X size={16} /></button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto pg-scroll">
              <p className="text-sm text-gray-300 leading-relaxed">{g.intro}</p>
              <ol className="mt-4 space-y-2.5">
                {g.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#F2BC2E]/15 text-[#F2BC2E] text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            <div className="p-3 border-t border-white/5 flex items-center gap-2">
              {speaking ? (
                <button onClick={stop} data-testid="guide-stop" className="flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold border border-[#F10D19]/40 text-[#F10D19] hover:bg-[#F10D19]/10 transition-colors">
                  <VolumeX size={15} /> {t("Arrêter")}
                </button>
              ) : (
                <button onClick={speak} disabled={loadingVoice} data-testid="guide-listen" className="flex-1 flex items-center justify-center gap-2 btn-gold rounded-full py-2.5 text-sm disabled:opacity-60">
                  {loadingVoice ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />} {loadingVoice ? t("Préparation…") : t("Écouter le guide")}
                </button>
              )}
              <button onClick={() => { setOpen(false); stop(); navigate("/assistant-ia"); }} data-testid="guide-ai-help"
                className="flex items-center justify-center gap-1.5 rounded-full py-2.5 px-3 text-sm font-semibold border border-white/15 text-gray-300 hover:bg-white/5 transition-colors">
                <HelpCircle size={15} /> {t("Aide IA")}
              </button>
            </div>
            <button data-testid="guide-report-issue"
              onClick={() => { setOpen(false); stop(); setReportOpen(true); }}
              className="w-full text-center py-2 text-xs text-gray-500 hover:text-[#F10D19] border-t border-white/5 transition-colors">
              {t("Signaler un problème")}
            </button>
          </div>
        </>
      )}

      <Modal open={reportOpen} onClose={() => setReportOpen(false)} title={t("Signaler un problème")}>
        <div className="space-y-3">
          <p className="text-sm text-gray-400">{t("Décrivez le problème rencontré. Le propriétaire le recevra dans le Centre Maître.")}</p>
          <textarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={4} data-testid="report-issue-text"
            className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F2BC2E] focus:outline-none" />
          <GoldButton onClick={submitReport} className="w-full justify-center" data-testid="report-issue-submit"><Send size={15} /> {t("Envoyer")}</GoldButton>
        </div>
      </Modal>
    </div>
  );
}
